import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, History, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { listWorkflowVersions, WorkflowVersion } from "../api";
import { cn } from "../../../lib/utils";

interface Props {
  workflowId: string;
  /** The workflow's current published version, for the "current" marker. */
  currentVersion?: number;
  status?: string;
  /** Load this snapshot onto the studio canvas. Omit for a read-only list. */
  onSelectVersion?: (version: WorkflowVersion) => void;
  /** "badge" = the studio's status chip; "button" = a standalone Versions button. */
  triggerVariant?: "badge" | "button";
  /** Which edge of the trigger the dropdown aligns to. */
  align?: "left" | "right";
}

/**
 * History of a workflow's frozen snapshots (call_workflow_versions). Used in
 * the studio header (badge, click-to-load) and on the runs page (button,
 * read-only).
 */
export function WorkflowVersionsMenu({
  workflowId,
  currentVersion,
  status,
  onSelectVersion,
  triggerVariant = "badge",
  align = "left",
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: versions = [], isLoading, isError } = useQuery({
    queryKey: ["call-workflow-versions", workflowId],
    queryFn: () => listWorkflowVersions(workflowId),
    enabled: open && !!workflowId,
  });

  const selectable = !!onSelectVersion;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    // Capture phase: the React Flow canvas stops propagation on its own
    // pointer handling (for panning), which would otherwise stop a
    // bubble-phase document listener from ever seeing a click on the canvas.
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open]);

  const badgeLabel =
    status === "draft" ? "DRAFT" : `PUBLISHED v${currentVersion || 1}`;

  return (
    <div className="relative" ref={ref}>
      {triggerVariant === "button" ? (
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 rounded-xl border border-line bg-elevated px-3 py-1.5 text-xs font-semibold text-ink-2 shadow-2xs hover:bg-soft transition-colors"
          title="Version history"
        >
          <History className="h-3.5 w-3.5" />
          Versions
          {currentVersion ? <span className="text-ink-3">v{currentVersion}</span> : null}
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
        </button>
      ) : (
        <button
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors",
            status === "draft"
              ? "bg-warning-soft text-warning hover:bg-warning-soft"
              : "bg-success-soft text-success hover:bg-success-soft",
          )}
          title="Version history"
        >
          {badgeLabel}
          <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
        </button>
      )}

      {open && (
        <div className={cn(
          "absolute top-full z-50 mt-1.5 w-80 overflow-hidden rounded-xl border border-line bg-elevated shadow-xl",
          align === "right" ? "right-0" : "left-0",
        )}>
          <div className="flex items-center gap-1.5 border-b border-line bg-soft px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-ink-3">
            <History className="h-3.5 w-3.5" />
            Version history
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 p-6 text-xs text-ink-3">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading versions…
              </div>
            ) : isError ? (
              <div className="p-6 text-center text-xs text-danger">Couldn't load version history.</div>
            ) : versions.length === 0 ? (
              <div className="p-6 text-center text-xs text-ink-3">
                No published versions yet. Publish this workflow to create v1.
              </div>
            ) : (
              <>
              <ul className="divide-y divide-line">
                {versions.map((v) => {
                  const isCurrent = v.workflow_version === currentVersion;
                  const RowInner = (
                    <>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-bold text-ink">v{v.workflow_version}</span>
                          {isCurrent && (
                            <span className="rounded-full bg-success-soft border border-success/30 px-1.5 py-0.5 text-[9px] font-bold uppercase text-success">
                              Live
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[11px] text-ink-3">
                          {v.node_count} step{v.node_count === 1 ? "" : "s"} · {v.edge_count} branch
                          {v.edge_count === 1 ? "" : "es"}
                        </p>
                        {v.published_by && (
                          <p className="text-[10px] font-mono text-ink-3 truncate">by {v.published_by}</p>
                        )}
                      </div>
                      <span className="shrink-0 text-[10px] text-ink-3 text-right">
                        {v.published_at
                          ? formatDistanceToNow(new Date(v.published_at), { addSuffix: true })
                          : "—"}
                      </span>
                    </>
                  );
                  return (
                    <li key={v.workflow_version}>
                      {selectable ? (
                        <button
                          type="button"
                          onClick={() => { onSelectVersion?.(v); setOpen(false); }}
                          className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left hover:bg-brand-soft/60 transition-colors"
                        >
                          {RowInner}
                        </button>
                      ) : (
                        <div className="flex items-start justify-between gap-3 px-3 py-2.5">{RowInner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {selectable && (
                <p className="border-t border-line px-3 py-2 text-[10px] text-ink-3">
                  Click a version to load it onto the canvas. Save to make it the working draft.
                </p>
              )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
